import { useState, useRef } from 'react';

import { useEffect } from 'react';

export function useCountdownTimer(initialSeconds: number, autoStart = true) {
  const [timer, setTimer] = useState(autoStart ? initialSeconds : 0);
  const [isRunning, setIsRunning] = useState(autoStart);
  const startTimeRef = useRef<Date | null>(null);
  const remainingRef = useRef<number>(initialSeconds);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (isRunning && timer > 0) {
      if (!startTimeRef.current) {
        startTimeRef.current = new Date();
      }
      intervalRef.current = setInterval(() => {
        if (startTimeRef.current) {
          const now = new Date();
          const elapsed = Math.floor((now.getTime() - startTimeRef.current.getTime()) / 1000);
          const newTimer = Math.max(remainingRef.current - elapsed, 0);
          setTimer(newTimer);
          if (newTimer <= 0) {
            setIsRunning(false);
            clearInterval(intervalRef.current!);
          }
        }
      }, 100);
      return () => {
        if (intervalRef.current) clearInterval(intervalRef.current);
      };
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [isRunning, timer]);

  const startTimer = () => {
    startTimeRef.current = new Date();
    remainingRef.current = initialSeconds;
    setTimer(initialSeconds);
    setIsRunning(true);
  };

  const stopTimer = () => {
    setIsRunning(false);
    if (startTimeRef.current) {
      const now = new Date();
      const elapsed = Math.floor((now.getTime() - startTimeRef.current.getTime()) / 1000);
      remainingRef.current = Math.max(remainingRef.current - elapsed, 0);
    }
    startTimeRef.current = null;
  };

  const resetTimer = () => {
    setIsRunning(false);
    setTimer(initialSeconds);
    remainingRef.current = initialSeconds;
    startTimeRef.current = null;
  };

  const formatTime = () => {
    if (timer <= 0) return '';

    const minutes = Math.floor(timer / 60)
      .toString()
      .padStart(2, '0');
    const seconds = (timer % 60).toString().padStart(2, '0');
    return `${minutes}:${seconds}`;
  };

  return {
    timer,
    isRunning,
    isFinished: timer <= 0,
    startTimer,
    stopTimer,
    resetTimer,
    formatTime,
  };
}
